/* ============================================================================
 * MasterCase — Zone-Aware Per-Unit Pricing (Dynamic Pricing Engine)
 * ----------------------------------------------------------------------------
 * The four global percentages (product markup + three per-market shipping
 * markups) live on `window.MasterCaseConfig`, injected by layout/theme.liquid
 * from theme customizer settings — single source of truth across the store.
 *
 * Per-product data attributes carry SOURCE INPUTS only:
 *
 *   data-vendor-wholesale       per-case vendor wholesale (CAD)
 *   data-shippo-rates           JSON: {"BC":15.50,"AB":17.20,...} Shippo base rates per zone
 *   data-case-size              units per case (integer)
 *   data-base-unit-price        fallback per-unit price (variant price) if source inputs missing
 *   data-msrp-unit-price        per-unit MSRP (for cart savings display)
 *   data-override-case-prices   OPTIONAL JSON: {"BC":"60.00",...} hard-override per zone (escape hatch)
 *
 * Per-zone case price is computed at render time:
 *
 *   case_price[zone] = (vendor_wholesale × (1 + product_markup/100))
 *                    + (shippo_base[zone] × (1 + zone_markup/100))
 *   unit_price[zone] = case_price[zone] / case_size
 *
 * Zone → market markup mapping:
 *   Western  (BC, AB)         → westernShippingMarkupPct
 *   Central  (SK, MB, ON, QC) → centralShippingMarkupPct
 *   Eastern  (AT, NL)         → easternShippingMarkupPct
 *
 * Element opt-in attributes:
 *   PDP/card headline:
 *     [data-shippo-rates] [data-vendor-wholesale] [data-case-size] [data-base-unit-price]
 *       Children:
 *       <span data-zone-unit-price>...</span>   ← per-unit price gets written here
 *
 *   PDP Add-to-Cart button:
 *     [data-zone-atc-button] [data-shippo-rates] [data-vendor-wholesale]
 *     [data-case-size] [data-base-unit-price]
 *       Children:
 *       <span data-zone-atc-total>...</span>   ← total = per-unit × selected qty
 *
 *   PDP quantity selector:
 *     <select data-zone-qty-select>
 *
 *   Cart line:
 *     <tr data-cart-line data-quantity="8">
 *       <td>...
 *         <td [data-shippo-rates] [data-vendor-wholesale] [data-case-size] [data-base-unit-price]>
 *           <span data-zone-unit-price>...</span> (per-unit headline)
 *         </td>
 *         <input data-cart-qty>
 *         <td data-zone-line-total
 *             [data-shippo-rates] [data-vendor-wholesale]
 *             [data-case-size] [data-base-unit-price] [data-msrp-unit-price]>...</td>
 *
 *   Cart summary:
 *     [data-zone-cart-subtotal]       - innerHTML rewritten with zone total
 *     [data-zone-cart-total]          - same value (no tax/ship displayed pre-checkout)
 *     [data-zone-cart-msrp-savings]   - shows "You save $X off MSRP (Y%)"
 *
 * Events:
 *   mc:postal-code-changed → update all prices to new zone
 *   mc:postal-code-cleared → revert to base (variant) prices
 * ============================================================================ */

(function () {
  'use strict';

  // Bilingual: the French storefront (mastercase.ca/fr) sets <html lang="fr">,
  // so we branch every customer-facing string off that. Apostrophes in the
  // French strings use the typographic ’ so they never close a JS quote.
  const FR = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;

  // Full-name labels for the headline "Delivering to ..." cue under the price.
  // Keep in sync with POSTAL_ZONE_MAP in sections/postal-code-bar.liquid.
  const ZONE_LABEL_FULL = FR ? {
    'BC':    'Livraison en Colombie-Britannique',
    'AB':    'Livraison en Alberta',
    'SK':    'Livraison en Saskatchewan',
    'MB':    'Livraison au Manitoba',
    'ON':    'Livraison en Ontario',
    'QC':    'Livraison au Québec',
    'AT':    'Livraison au Canada atlantique',
    'NL':    'Livraison à Terre-Neuve-et-Labrador'
  } : {
    'BC':    'Delivering to British Columbia',
    'AB':    'Delivering to Alberta',
    'SK':    'Delivering to Saskatchewan',
    'MB':    'Delivering to Manitoba',
    'ON':    'Delivering to Ontario',
    'QC':    'Delivering to Quebec',
    'AT':    'Delivering to Atlantic Canada',
    'NL':    'Delivering to Newfoundland & Labrador'
  };
  const NORTH_NOTICE = FR
    ? 'Nous ne livrons pas encore dans les territoires.'
    : 'We don’t ship to the territories yet.';

  // Fallback defaults so the storefront still computes prices if theme.liquid
  // didn't inject MasterCaseConfig for some reason. Updated 2026-06-01: product
  // markup converted from 2-tier 18/12 to 3-tier progressive 17 / 15 / 12 with
  // breaks at $100 and $200 wholesale.
  const CONFIG_DEFAULTS = {
    tier1MarkupPct:           20,   // applies to wholesale up to break 1
    tierBreak1Wholesale:     100,
    tier2MarkupPct:           18,   // applies to wholesale between break 1 and break 2
    tierBreak2Wholesale:     200,
    tier3MarkupPct:           16,   // applies to wholesale above break 2
    westernShippingMarkupPct: 29,
    centralShippingMarkupPct: 18,
    easternShippingMarkupPct: 15
  };

  function getConfig() {
    const injected = (typeof window !== 'undefined' && window.MasterCaseConfig) || {};
    return {
      tier1MarkupPct:           numOrDefault(injected.tier1MarkupPct,           CONFIG_DEFAULTS.tier1MarkupPct),
      tierBreak1Wholesale:      numOrDefault(injected.tierBreak1Wholesale,      CONFIG_DEFAULTS.tierBreak1Wholesale),
      tier2MarkupPct:           numOrDefault(injected.tier2MarkupPct,           CONFIG_DEFAULTS.tier2MarkupPct),
      tierBreak2Wholesale:      numOrDefault(injected.tierBreak2Wholesale,      CONFIG_DEFAULTS.tierBreak2Wholesale),
      tier3MarkupPct:           numOrDefault(injected.tier3MarkupPct,           CONFIG_DEFAULTS.tier3MarkupPct),
      westernShippingMarkupPct: numOrDefault(injected.westernShippingMarkupPct, CONFIG_DEFAULTS.westernShippingMarkupPct),
      centralShippingMarkupPct: numOrDefault(injected.centralShippingMarkupPct, CONFIG_DEFAULTS.centralShippingMarkupPct),
      easternShippingMarkupPct: numOrDefault(injected.easternShippingMarkupPct, CONFIG_DEFAULTS.easternShippingMarkupPct)
    };
  }

  // Progressive product markup — 3 brackets, cliff-free. Each dollar of vendor
  // wholesale is taxed at the rate of the bracket it falls in:
  //   wholesale ≤ break1               → all of it at tier1
  //   break1 < wholesale ≤ break2      → first $break1 at tier1, rest at tier2
  //   wholesale > break2               → first $break1 at tier1, next ($break2-$break1) at tier2, rest at tier3
  function productMargin(wholesale, cfg) {
    const t1 = (cfg.tier1MarkupPct || 0) / 100;
    const t2 = (cfg.tier2MarkupPct || 0) / 100;
    const t3 = (cfg.tier3MarkupPct || 0) / 100;
    const b1 = cfg.tierBreak1Wholesale || 0;
    const b2 = cfg.tierBreak2Wholesale || 0;
    if (wholesale <= b1) return wholesale * t1;
    if (wholesale <= b2) return (b1 * t1) + ((wholesale - b1) * t2);
    return (b1 * t1) + ((b2 - b1) * t2) + ((wholesale - b2) * t3);
  }

  function numOrDefault(v, d) {
    const n = parseFloat(v);
    return isNaN(n) ? d : n;
  }

  function zoneMarkupPct(zone, config) {
    if (zone === 'BC' || zone === 'AB') return config.westernShippingMarkupPct;
    if (zone === 'SK' || zone === 'MB' || zone === 'ON' || zone === 'QC') return config.centralShippingMarkupPct;
    if (zone === 'AT' || zone === 'NL') return config.easternShippingMarkupPct;
    return 0;
  }

  // Pure computation: case price for a given zone from source inputs + global
  // config. The single source of truth for the pricing formula. Both the
  // storefront's resolveUnitPrice() and the vendor-calculator page call this.
  function computeCasePrice(wholesale, shippoRate, zone, config) {
    const cfg = config || getConfig();
    const zoneMarkup = zoneMarkupPct(zone, cfg) / 100;
    return wholesale + productMargin(wholesale, cfg) + shippoRate * (1 + zoneMarkup);
  }

  // MasterCase Net per case: profit kept after paying vendor wholesale, Shippo,
  // and payment processing fees. Used by the vendor-calculator gate logic and any future
  // admin economics tool that needs to score per-product, per-zone profitability.
  function computeMcNet(wholesale, shippoRate, zone, config) {
    const cfg = config || getConfig();
    const casePrice      = computeCasePrice(wholesale, shippoRate, zone, cfg);
    const processingFee  = casePrice * 0.029 + 0.30; // rate carried over from the previous processor, unverified for Shopify Payments
    const prodMargin     = productMargin(wholesale, cfg);
    const shippingMargin = shippoRate * (zoneMarkupPct(zone, cfg) / 100);
    return prodMargin + shippingMargin - processingFee;
  }

  function getCurrentZone() {
    try { return localStorage.getItem('mc_zone'); } catch (e) { return null; }
  }
  function getCurrentPostal() {
    try { return localStorage.getItem('mc_postal_code'); } catch (e) { return null; }
  }

  function parseJSONAttr(el, attr) {
    const raw = el.getAttribute(attr);
    if (!raw) return null;
    try { return JSON.parse(raw); }
    catch (e) {
      console.warn('[mc-zone-pricing] invalid JSON in ' + attr, raw);
      return null;
    }
  }

  // ── Per-product shipping region allowlist ───────────────────────────────────
  // A product may restrict the provinces it ships to (e.g. frozen items that
  // can only travel a short distance from origin). The allowlist lives on a
  // [data-ship-to-zones] attribute as a JSON array (["BC","AB"]) or a plain
  // "BC,AB" string. Absent/empty = unrestricted (ships to every served zone).
  //
  // Province codes are normalised to the runtime ZONE codes the postal bar
  // actually emits: NB/NS/PE fold into AT.
  //
  // 🔴 MB NO LONGER FOLDS INTO SK (2026-10-31). Manitoba is its own zone now, so
  // "MB" in an allowlist means Manitoba and "SK" means Saskatchewan alone. Before
  // this change the two were the same code and either spelling covered both.
  // 🔑 Checked against all 575 active products before splitting them: only 8 carry
  // an allowlist at all, every one reads "BC,AB,SK,MB", so all 8 name Manitoba
  // explicitly and none lost coverage. ⚠️ Any NEW allowlist meaning "the prairies"
  // must now list BOTH SK and MB.
  const ZONE_ALIAS = { NB: 'AT', NS: 'AT', PE: 'AT', PEI: 'AT' };

  function shipToZones(el) {
    const raw = el.getAttribute('data-ship-to-zones');
    if (!raw || !raw.trim()) return null;
    let list = null;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed))        list = parsed;
      else if (typeof parsed === 'string') list = parsed.split(/[,\s]+/);
    } catch (e) {
      list = raw.split(/[,\s]+/);
    }
    if (!list) return null;
    list = list
      .map(function (z) {
        const code = String(z).trim().toUpperCase();
        return ZONE_ALIAS[code] || code;
      })
      .filter(Boolean);
    return list.length ? list : null;
  }

  // True if this product element can ship to `zone`. Territories (NORTH) are
  // never serviceable. Unrestricted products ship to every non-NORTH zone;
  // restricted products ship only to the zones on their allowlist.
  function zoneServiceable(el, zone) {
    if (!zone || zone === 'NORTH') return false;
    const allow = shipToZones(el);
    if (!allow) return true;
    return allow.indexOf(zone) !== -1;
  }

  // Returns the per-unit price to use for the given zone (or the default
  // display zone if none is set). Reads source inputs and computes live.
  //
  // Default display zone is Central Canada (ON) — the largest market by
  // population, so most pre-postal-code visitors see their actual price.
  // Once a postal code is entered, the resolved zone takes over.
  const DEFAULT_DISPLAY_ZONE = 'ON';

  function resolveUnitPrice(el, zone) {
    const caseSize      = parseInt(el.getAttribute('data-case-size'), 10) || 1;
    const baseUnitPrice = parseFloat(el.getAttribute('data-base-unit-price')) || 0;
    const effectiveZone = zone || DEFAULT_DISPLAY_ZONE;

    // ── FLAT PRICING (locked 2026-06-09) ───────────────────────────────────
    // Honesty rule: the price SHOWN must equal the price CHARGED. Shopify bills
    // the single native variant price at checkout and JS cannot change that, so
    // we no longer rewrite the displayed price per zone — every shopper sees the
    // one flat price they will actually pay. The postal-code bar still drives
    // DELIVERY gating (zoneServiceable / ship-to-zones), just never price.
    // To re-enable true per-zone charging later, a checkout-side Cart Transform
    // function must exist first; then delete this early return.
    return baseUnitPrice;
    // ────────────────────────────────────────────────────────────────────────

    // NORTH (territories) and unknown zones fall back to base price.
    if (effectiveZone === 'NORTH') return baseUnitPrice;

    // Escape hatch: per-SKU hard overrides win over the formula.
    const overrides = parseJSONAttr(el, 'data-override-case-prices');
    if (overrides && overrides[effectiveZone] != null) {
      const override = parseFloat(overrides[effectiveZone]);
      if (!isNaN(override) && caseSize > 0) {
        return override / caseSize;
      }
    }

    const vendorWholesale = parseFloat(el.getAttribute('data-vendor-wholesale'));
    const shippoRates     = parseJSONAttr(el, 'data-shippo-rates');

    if (isNaN(vendorWholesale) || !shippoRates || shippoRates[effectiveZone] == null) {
      return baseUnitPrice;
    }

    const shippoRate = parseFloat(shippoRates[effectiveZone]);
    if (isNaN(shippoRate) || caseSize <= 0) return baseUnitPrice;

    const casePrice = computeCasePrice(vendorWholesale, shippoRate, effectiveZone, getConfig());
    return casePrice / caseSize;
  }

  function fmt(n) { return '$' + n.toFixed(2); }

  // Elements with zone-aware pricing data carry data-shippo-rates (the new
  // source-input attribute). Backward-compat: also pick up elements that only
  // have data-base-unit-price + data-case-size in case a template hasn't been
  // refactored yet — they'll render the base price.
  function selectAllPriceEls() {
    return document.querySelectorAll('[data-shippo-rates][data-case-size]');
  }

  // ── HEADLINE PRICE ELEMENTS (PDP, card, cart unit price column) ───────────
  function updateHeadlinePrices(zone) {
    selectAllPriceEls().forEach(function (el) {
      // Skip ATC buttons and line totals — they're handled separately
      if (el.hasAttribute('data-zone-atc-button')) return;
      if (el.hasAttribute('data-zone-line-total')) return;

      const target = el.querySelector('[data-zone-unit-price]');
      if (!target) return;

      // The PDP also carries a case headline. It is derived from the SAME
      // resolved unit price, never from a stored figure, so a zone change moves
      // both numbers together. Tiles and the cart have no such element, which is
      // why they are untouched by this.
      const caseTarget = el.querySelector('[data-zone-case-price]');
      const caseSizeEl = parseInt(el.getAttribute('data-case-size'), 10) || 1;

      // Don't show a price we won't honour in this region.
      if (zone && !zoneServiceable(el, zone)) {
        target.textContent = '—';
        if (caseTarget) caseTarget.textContent = '—';
        return;
      }
      const resolvedUnit = resolveUnitPrice(el, zone);
      target.textContent = resolvedUnit.toFixed(2);
      if (caseTarget) caseTarget.textContent = (resolvedUnit * caseSizeEl).toFixed(2);
    });
  }

  // ── PDP ADD-TO-CART BUTTON ────────────────────────────────────────────────
  // Also blocks add-to-cart when zone is NORTH (territories) — replaces the
  // button label with a contact-us message and disables submit, so a visitor
  // can't walk a NWT/Nunavut/Yukon order through to checkout.
  function updateATCButton(zone) {
    document.querySelectorAll('[data-zone-atc-button]').forEach(function (btn) {
      // Block add-to-cart when this product can't ship to the resolved zone —
      // territories (NORTH) or a province outside the product's allowlist. An
      // unknown zone (no postal code entered yet) is NOT blocked; the Shopify
      // shipping profile stays the hard backstop at checkout.
      const blocked = zone && !zoneServiceable(btn, zone);

      if (blocked) {
        if (!btn.dataset.mcOrigInner) btn.dataset.mcOrigInner = btn.innerHTML;
        if (!btn.dataset.mcOrigBg)    btn.dataset.mcOrigBg    = btn.style.background || '';
        btn.disabled       = true;
        btn.style.background = '#9CA3AF';
        btn.style.cursor     = 'not-allowed';
        btn.innerHTML        = (zone === 'NORTH')
          ? (FR ? 'Hors de notre zone de service' : 'Outside our service area')
          : (FR ? 'Pas encore offert dans votre région' : 'Not available in your region yet');
        btn.setAttribute('data-mc-zone-blocked', '1');
        return;
      }
      if (btn.getAttribute('data-mc-zone-blocked') === '1') {
        btn.disabled       = false;
        btn.style.cursor   = '';
        btn.style.background = btn.dataset.mcOrigBg || '';
        if (btn.dataset.mcOrigInner) btn.innerHTML = btn.dataset.mcOrigInner;
        delete btn.dataset.mcOrigInner;
        delete btn.dataset.mcOrigBg;
        btn.removeAttribute('data-mc-zone-blocked');
      }
      const unitPrice = resolveUnitPrice(btn, zone);
      const qtySelect = document.querySelector('[data-zone-qty-select]');
      /* 🔴 A "Case" ladder (Xero Tabs 2/4/6, Harlow dual-size) renders a HIDDEN quantity
         input instead of this select, so `qtySelect ? ... : 1` fell back to ONE UNIT and
         then OVERWROTE the correct Liquid total on load. Live on 2026-09-27: the button
         read "Add to cart · $50.99" where $101.98 was owed, and was wrong at every rung
         (47.99 for 191.96, 44.99 for 269.94). Same root cause as the cart mischarge fixed
         the same day: never assume the select exists — read whichever control the product
         actually has, and fall back to the case size, never to 1. */
      const qtyHidden = document.querySelector('[data-mc-qty-hidden]');
      const qtyEl     = qtySelect || qtyHidden;
      const qty       = qtyEl ? (parseInt(qtyEl.value, 10) || 0)
                              : (parseInt(btn.getAttribute('data-case-size'), 10) || 1);
      const total     = unitPrice * qty;
      const target    = btn.querySelector('[data-zone-atc-total]');
      if (target) target.textContent = total.toFixed(2);
    });
  }

  // ── CART RECONCILIATION ────────────────────────────────────────────────────
  function updateCart(zone) {
    const cartEl = document.querySelector('[data-zone-cart]');
    if (!cartEl) return;

    let zoneSubtotalCents = 0;
    let msrpSubtotalCents = 0;

    cartEl.querySelectorAll('[data-cart-line]').forEach(function (row) {
      const qty = parseInt(row.getAttribute('data-quantity'), 10) || 0;
      const lineTotalEl = row.querySelector('[data-zone-line-total]');
      if (!lineTotalEl) return;

      const unitPrice    = resolveUnitPrice(lineTotalEl, zone);
      const msrpUnitPrice = parseFloat(lineTotalEl.getAttribute('data-msrp-unit-price'))
                            || parseFloat(lineTotalEl.getAttribute('data-base-unit-price'))
                            || 0;

      const lineTotal = unitPrice * qty;
      lineTotalEl.innerHTML = fmt(lineTotal);

      zoneSubtotalCents += Math.round(lineTotal * 100);
      msrpSubtotalCents += Math.round(msrpUnitPrice * qty * 100);

      // Also update the per-unit headline cell in this row (it has its own
      // data-shippo-rates on the unit-price <td>)
      const unitPriceCell = row.querySelector('[data-shippo-rates]:not([data-zone-line-total])');
      if (unitPriceCell) {
        const unitTarget = unitPriceCell.querySelector('[data-zone-unit-price]');
        if (unitTarget) unitTarget.textContent = unitPrice.toFixed(2);
      }
    });

    const subtotalEl    = document.querySelector('[data-zone-cart-subtotal]');
    const totalEl       = document.querySelector('[data-zone-cart-total]');
    const msrpSavingsEl = document.querySelector('[data-zone-cart-msrp-savings]');

    // 2026-10-25: this JS no longer writes the order summary. Liquid already
    // renders cart.items_subtotal_price and cart.total_price, which are the
    // numbers checkout uses. resolveUnitPrice returns the flat base price and
    // exits early, so recomputing here could only ever agree with Liquid or be
    // wrong. On 2026-10-24 it was wrong on Mike's machine, showing six times
    // the real total while the per-line figures were correct, and it could not
    // be reproduced anywhere else. The summary is the one number a customer
    // checks against their card, so it is server rendered only. Do not restore
    // these two writes without a Cart Transform making zone prices real.
    void subtotalEl; void totalEl;

    if (msrpSavingsEl) {
      if (msrpSubtotalCents > zoneSubtotalCents) {
        const savedCents = msrpSubtotalCents - zoneSubtotalCents;
        const saved      = savedCents / 100;
        const savedPct   = Math.floor((savedCents / msrpSubtotalCents) * 100);
        msrpSavingsEl.innerHTML = FR
          ? 'Vous économisez ' + saved.toFixed(2) + ' $ sur le PDSF <span style="font-weight:400">(' + savedPct.toFixed(0) + ' %)</span>'
          : 'You save $' + saved.toFixed(2) + ' off retail MSRP <span style="font-weight:400">(' + savedPct.toFixed(0) + '% off)</span>';
        msrpSavingsEl.style.display = 'block';
      } else {
        msrpSavingsEl.style.display = 'none';
      }
    }
  }

  // ── ZONE LABEL & NORTH FALLBACK ────────────────────────────────────────────
  // Populates any [data-zone-label] element with a short reassurance line
  // ("Delivering to BC") and switches it to a red NORTH-out-of-area notice
  // when the resolved zone has no priced market.
  function updateZoneLabels(zone) {
    document.querySelectorAll('[data-zone-label]').forEach(function (el) {
      if (!zone) {
        el.textContent = '';
        el.removeAttribute('data-zone-state');
        el.style.background = '';
        el.style.color = '';
        el.style.padding = '';
        el.style.borderRadius = '';
        el.style.fontSize = '';
        return;
      }
      if (zone === 'NORTH') {
        el.textContent = NORTH_NOTICE;
        el.setAttribute('data-zone-state', 'unsupported');
        el.style.background    = '#FDECEA';
        el.style.color         = '#C0392B';
        el.style.padding       = '10px 14px';
        el.style.borderRadius  = '4px';
        el.style.fontSize      = '13px';
        return;
      }
      // Product carries a region allowlist and this zone isn't on it.
      if (el.hasAttribute('data-ship-to-zones') && !zoneServiceable(el, zone)) {
        el.textContent = FR ? 'Nous ne livrons pas encore dans votre région.' : 'We don’t deliver to your region yet.';
        el.setAttribute('data-zone-state', 'unsupported');
        el.style.background    = '#FDECEA';
        el.style.color         = '#C0392B';
        el.style.padding       = '10px 14px';
        el.style.borderRadius  = '4px';
        el.style.fontSize      = '13px';
        return;
      }
      const label = ZONE_LABEL_FULL[zone] || ((FR ? 'Livraison — ' : 'Delivering to ') + zone);
      el.textContent = label;
      el.setAttribute('data-zone-state', 'ok');
      el.style.background    = '';
      el.style.color         = '';
      el.style.padding       = '';
      el.style.borderRadius  = '';
      el.style.fontSize      = '';
    });
  }

  // ── SHIPPING NOTE (zone-aware, honest) ───────────────────────────────────────
  // Free for West+Central; East (AT/NL) sees "shipping at checkout ~$X" UP FRONT so
  // an Atlantic/NL shopper is never surprised at checkout. Unserviceable (per-product
  // fence / territories) → "not available in your region". No postal yet → soft default.
  function updateShippingNotes(zone) {
    document.querySelectorAll('[data-zone-shipping-note]').forEach(function (el) {
      const inner = el.querySelector('[data-zone-shipping-text]') || el;
      if (zone && !zoneServiceable(el, zone)) {
        el.style.background = '#FDECEA';
        inner.innerHTML = (zone === 'NORTH')
          ? (FR ? '<strong style="color:#C0392B">Nous ne livrons pas encore dans les territoires.</strong>' : '<strong style="color:#C0392B">We don’t ship to the territories yet.</strong>')
          : (FR ? '<strong style="color:#C0392B">Pas encore offert dans votre région.</strong> Nous prenons de l’expansion, revenez bientôt.' : '<strong style="color:#C0392B">Not available in your region yet.</strong> We’re expanding — check back soon.');
        return;
      }
      // FROZEN wins over every other message. Frozen prices carry no freight (cost + ~15%),
      // so the fulfiller's $40 allowance is the only cover and anything above it is charged
      // at checkout. Saying "shipped free, no surprises" on a cart holding frozen is a promise
      // we cannot keep on a rural lane, where a label can run well past $100.
      if (el.hasAttribute('data-has-frozen')) {
        el.style.background = '#F1F8F4';
        inner.innerHTML = FR
          ? '<strong style="color:#2E7D32">Comprend 40 $ pour la livraison des produits surgelés.</strong> Coût final confirmé au paiement.'
          : '<strong style="color:#2E7D32">Includes $40 toward frozen shipping.</strong> Final cost confirmed at checkout.';
        return;
      }
      el.style.background = '#F1F8F4';
      // 🔒 ON THE CART the per-line badge already says "Most cases ship free" on every
      //    case, so repeating it above the checkout button buys nothing. Mike 2026-10-22:
      //    use that space for the damage guarantee instead. Product pages keep the
      //    shipping note, which is the only place that fact appears there.
      //    ⚠️ This branch is only reached when the zone IS serviceable and the cart holds
      //    no frozen: both of those warnings return above and still take priority.
      if (el.hasAttribute('data-cart-guarantee')) {
        inner.innerHTML = FR
          ? '<strong style="color:#2E7D32">Article abîmé, défectueux ou erroné&nbsp;?</strong> Envoyez-nous une photo et nous le remboursons ou le remplaçons.'
          : '<strong style="color:#2E7D32">Damaged, defective or wrong?</strong> Send us a photo and we’ll refund or replace it.';
        return;
      }
      // 🔒 THE EASTERN BRANCH IS GONE (2026-10-02). It promised "a Canada Post weight
      // surcharge (from $15) applies to your region" — a charge DELETED on 2026-09-30 when
      // the eastern weight surcharge was retired and one national price took over. Out of
      // Mississauga, Halifax costs $21.30 against Vancouver's $21.36, so there was never a
      // distance to bill for. AT and NL now get the same message as everyone else.
      if (zone) {
        inner.innerHTML = FR
          ? '<strong style="color:#2E7D32">Livraison gratuite sur la plupart des caisses.</strong> Tout frais de livraison est affiché au paiement.'
          : '<strong style="color:#2E7D32">Most cases ship free.</strong> Any shipping charge is shown at checkout.';
      } else {
        inner.innerHTML = FR
          ? '<strong style="color:#2E7D32">Livraison gratuite sur la plupart des caisses.</strong> Entrez votre code postal pour confirmer votre région.'
          : '<strong style="color:#2E7D32">Most cases ship free.</strong> Enter your postal code to confirm your area.';
      }
    });
  }

  // ── CART SUMMARY SHIPPING LABEL (zone-aware) ─────────────────────────────────
  // Keeps the cart order-summary "Shipping" value honest. It used to read "Free" for
  // West/Central and "At checkout" for Atlantic/NL, which tracked the eastern weight
  // surcharge. That surcharge is gone (2026-09-30) and "Free" is no longer true for anyone,
  // since the customer pays whatever a case exceeds $35 and only checkout knows it.
  function updateCartShippingLabel(zone) {
    document.querySelectorAll('[data-zone-cart-shipping]').forEach(function (el) {
      if (el.hasAttribute('data-has-frozen')) {          // frozen: never "Free", see updateShippingNotes
        el.textContent = FR ? 'Au paiement' : 'At checkout';
        el.style.color = '#2E7D32';
        return;
      }
      // 🔒 No east/west split any more, and "Free" is no longer honest for anyone: the
      // customer pays whatever a case's real carrier cost exceeds $35, and only checkout
      // knows that figure. One label for the whole country.
      el.textContent = FR ? 'Au paiement' : 'At checkout';
      el.style.color = '#2E7D32';
    });
  }

  // ── SAVINGS SUPPRESSION IN THE EAST: RETIRED 2026-10-02 ──────────────
  // This hid every savings figure in Atlantic Canada and Newfoundland. Correct when built
  // (2026-08-20): those provinces paid a $15/$30/$45/$60 weight surcharge at checkout on
  // top of the flat price, which ate the saving and on the lightest cases erased it.
  //
  // 🔒 THE SURCHARGE WAS DELETED 2026-09-30 and one national price replaced it. Out of
  // Mississauga, Halifax costs $21.30 against Vancouver $21.36, so there was never a
  // distance to bill for. The East now nets about $11.33 a case against BC $12.23 and
  // shows savings in line with the rest of the country. The reason was gone but the
  // mechanism was not, so a third of Canada saw a catalogue with every discount blanked
  // out. It used VISIBILITY rather than display, so cards looked normal and nobody
  // testing from BC could have seen it.
  //
  // 🔒 DO NOT REINSTATE unless a destination genuinely gets a lower saving. One price,
  // one savings figure, everywhere. The CSS rules it drove are gone from mastercase.css.
  function updateSavingsVisibility() {
    document.documentElement.classList.remove('mc-hide-savings');
  }

  // ── ORCHESTRATION ──────────────────────────────────────────────────────────
  function updateAll(zone) {
    updateSavingsVisibility(zone);
    updateHeadlinePrices(zone);
    updateATCButton(zone);
    updateCart(zone);
    updateZoneLabels(zone);
    updateShippingNotes(zone);
    updateCartShippingLabel(zone);
  }

  // Sync zone to cart attribute so checkout knows it (for eventual reconciliation)
  function syncCartAttribute(zone, postal) {
    if (!window.fetch || !window.routes) return;
    const cartUrl = (window.routes.cart_url || '/cart') + '/update.js';
    fetch(cartUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        attributes: { 'mc_zone': zone || '', 'mc_postal_code': postal || '' }
      })
    }).catch(function () { /* swallow */ });
  }

  // ── EVENT LISTENERS ────────────────────────────────────────────────────────
  document.addEventListener('mc:postal-code-changed', function (e) {
    const zone   = e.detail && e.detail.zone;
    const postal = e.detail && e.detail.postalCode;
    updateAll(zone);
    syncCartAttribute(zone, postal);
  });

  document.addEventListener('mc:postal-code-cleared', function () {
    updateAll(null);
    syncCartAttribute(null, null);
  });

  // PDP quantity dropdown change → re-render ATC button total
  document.addEventListener('change', function (e) {
    const t = e.target;
    if (t && t.matches && t.matches('[data-zone-qty-select]')) {
      updateATCButton(getCurrentZone());
    }
  });

  // ── CART LINE +/-/REMOVE BUTTONS (Faire-style) ─────────────────────────────
  // Each button updates the row optimistically then syncs to Shopify cart API.
  async function syncCartLineToServer(lineKey, newQty) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: newQty })
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.warn('[mc-zone-pricing] cart change failed', e);
      return null;
    }
  }

  document.addEventListener('click', async function (e) {
    const t = e.target;
    if (!t || !t.matches) return;

    const row = t.closest('[data-cart-line]');
    if (!row) return;

    // A Select-a-Case built from component lines is several cart lines shown as one row,
    // so Remove has to clear every line in the group, not just the first.
    const caseKeys = row.getAttribute('data-case-keys');
    if (caseKeys && t.matches('[data-qty-remove]')) {
      e.preventDefault();
      row.style.transition = 'opacity 0.2s';
      row.style.opacity = '0';
      let cart = null;
      for (const key of caseKeys.split(/\s+/).filter(Boolean)) {
        cart = await syncCartLineToServer(key, 0);
      }
      if (cart && typeof window.updateCartCount === 'function') window.updateCartCount(cart.item_count);
      if (cart && cart.item_count === 0) { window.location.reload(); return; }
      row.remove();
      updateCart(getCurrentZone());
      return;
    }

    const lineKey   = row.getAttribute('data-line-key');
    const caseSize  = parseInt(row.getAttribute('data-case-size'), 10) || 1;
    const currentQty = parseInt(row.getAttribute('data-quantity'), 10) || 0;

    let newQty = currentQty;
    let isRemove = false;

    if (t.matches('[data-qty-inc]')) {
      newQty = currentQty + caseSize;
    } else if (t.matches('[data-qty-dec]')) {
      newQty = currentQty - caseSize;
      if (newQty < caseSize) { newQty = 0; isRemove = true; }
    } else if (t.matches('[data-qty-remove]')) {
      newQty = 0;
      isRemove = true;
    } else {
      return;
    }

    e.preventDefault();

    // Optimistic UI update
    row.setAttribute('data-quantity', newQty);
    const qtyDisplay = row.querySelector('[data-qty-display]');
    if (qtyDisplay) qtyDisplay.textContent = newQty;

    // Update case-count subtitle if it exists in the row
    const caseCountEl = row.querySelector('[data-case-count]');
    if (caseCountEl) caseCountEl.textContent = Math.floor(newQty / caseSize);

    updateCart(getCurrentZone());

    const cart = await syncCartLineToServer(lineKey, newQty);

    if (cart && typeof window.updateCartCount === 'function') {
      window.updateCartCount(cart.item_count);
    }

    if (isRemove) {
      row.style.transition = 'opacity 0.2s';
      row.style.opacity = '0';
      setTimeout(function () {
        row.remove();
        // If no items left, reload to show empty state
        if (cart && cart.item_count === 0) {
          window.location.reload();
        }
      }, 200);
    }
  });

  // Initial application on page load. Also seeds the cart attribute from the
  // restored zone so backend reconciliation always sees a current value, not a
  // stale one from a previous session.
  function init() {
    const zone   = getCurrentZone();
    const postal = getCurrentPostal();
    updateAll(zone);
    if (zone) syncCartAttribute(zone, postal);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Cross-tab sync: localStorage propagates between tabs, but the rendered
  // prices in OTHER tabs don't auto-refresh. Listen for storage events so
  // setting a zone in tab A immediately updates pricing in tab B.
  window.addEventListener('storage', function (e) {
    if (e.key !== 'mc_zone' && e.key !== 'mc_postal_code') return;
    updateAll(getCurrentZone());
  });

  window.MasterCaseZonePricing = {
    updateAll: updateAll,
    resolveUnitPrice: resolveUnitPrice,
    computeCasePrice: computeCasePrice,
    computeMcNet: computeMcNet,
    productMargin: productMargin,
    zoneMarkupPct: zoneMarkupPct,
    getConfig: getConfig
  };
})();